// El Header o cabecera de la página.
import { useUser } from '../../context/UserContext';
import { db, auth } from '../../firebase/config'
import React, { useState, useEffect, useRef } from 'react';
import { 
    collection, query, where, doc, onSnapshot, Timestamp, addDoc, getDoc,
    writeBatch
} from 'firebase/firestore';
import { calculateVacationBalance, calculateBusinessDays} from '../../utils/helpers'
import { signOut } from 'firebase/auth';
import { toast } from 'react-toastify';

export const Header = ({ selectedRole, setSelectedRole, isWorking, selectedClientProfile, setSelectedClientProfile }) => {
    const logoGrupoEvelsa = "https://www.grupoevelsa.com/assets/images/Logo Evelsa 2.png";
    const { userData, user} = useUser();
    const hasMultipleRoles = userData?.roles && userData.roles.length > 1;
    const hasMultipleClients = userData?.clientesAsociados && userData.clientesAsociados.length > 1;
    const activeRole = selectedRole || userData?.rol;
    const [clientProfiles, setClientProfiles] = useState([]);
    const [showVacationModal, setShowVacationModal] = useState(false);
    const audioRef = useRef(new Audio("https://www.grupoevelsa.com/assets/notificacion.mp3"));

    const canAccessVacations = userData && 
                               userData.rol !== 'cliente' &&
                               userData.rol !== 'rh' && 
                               userData.rol !== 'administrador' &&
                               userData.rol !== 'directivo' &&
                               !userData.roles?.includes('administrador');

    const clientsKey = userData?.clientesAsociados?.join(',') || '';

    useEffect(() => {
        const clientIds = clientsKey ? clientsKey.split(',') : [];

        if (!user || clientIds.length <= 1) {
            setClientProfiles([]);
            return;
        }
        
        const fetchProfiles = async () => {
            try {
                const profilePromises = clientIds.map(id => getDoc(doc(db, "usuarios", id)));
                const profileSnapshots = await Promise.all(profilePromises);
                
                const validProfiles = profileSnapshots
                    .filter(snap => snap.exists())
                    .map(doc => ({ id: doc.id, ...doc.data() }));
                    
                setClientProfiles(validProfiles);
            } catch (error) {
                console.error("Error al obtener plantas asociadas:", error);
            }
        };
        fetchProfiles();

    }, [clientsKey, user]);

    useEffect(() => {
        if (clientProfiles.length > 0 && (!selectedClientProfile || !selectedClientProfile.id)) {
            const defaultProfile = clientProfiles.find(p => p.id === user?.uid) || clientProfiles[0];
            setSelectedClientProfile(defaultProfile);
        }
    }, [clientProfiles, selectedClientProfile, setSelectedClientProfile, user?.uid]);

    const handleSafeSignOut = () => {
        signOut(auth);
    };

    const handleClientChange = (e) => {
        const newProfileId = e.target.value;
        const profile = clientProfiles.find(p => p.id === newProfileId);
        if (profile) {
            setSelectedClientProfile(profile);
        }
    };

    useEffect(() => {
        if (!user?.uid) return;

        const q = query(
            collection(db, "notificaciones"),
            where("recipientId", "==", user.uid),
            where("read", "==", false)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const newNotifications = [];
            snapshot.docChanges().forEach((change) => {
                if (change.type === "added") {
                    newNotifications.push({ id: change.doc.id, ...change.doc.data() });
                }
            });

            if (newNotifications.length > 2) {
                toast.info(`Tienes ${newNotifications.length} notificaciones nuevas sin leer.`);
                audioRef.play().catch(e => console.log("La interacción del usuario es necesaria para reproducir sonido."));
            } else {
                newNotifications.forEach((notif, index) => {
                    setTimeout(() => {
                        toast.info(`${notif.message}`);
                        audioRef.play().catch(e => console.log("La interacción del usuario es necesaria para reproducir sonido."));
                    }, index * 1000);
                });
            }

            if (newNotifications.length > 0) {
                const batch = writeBatch(db);
                newNotifications.forEach(notif => {
                    const notifRef = doc(db, "notificaciones", notif.id);
                    batch.update(notifRef, { read: true });
                });
                batch.commit();
            }
        });

        return () => unsubscribe();
    }, [user]);

    const activeLogoUrl = selectedClientProfile?.logoUrl || userData?.logoUrl;

    return (
        <>
            <header className="bg-white shadow-md sticky top-0 z-40">
                <div className="max-w-7xl mx-auto py-3 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <div className="flex items-center">
                        {activeRole === 'cliente' && activeLogoUrl ? (
                            <img src={activeLogoUrl} onError={(e)=>{e.target.onerror = null; e.target.src="https://placehold.co/150x50/FFFFFF/000000?text=Logo+Cliente"}} alt="Logo Cliente" className="h-12 w-auto mr-4 object-contain"/>
                        ) : (
                            <img src={logoGrupoEvelsa} alt="Logo Grupo Evelsa" className="h-12 w-auto mr-4"/>
                        )}
                    </div>
                    
                    {user && (
                        <div className="flex items-center space-x-4">
                            {canAccessVacations ? (
                                <button 
                                    onClick={() => setShowVacationModal(true)}
                                    className="text-gray-600 hidden sm:flex items-center hover:text-blue-600 hover:bg-blue-50 px-2 py-1 rounded transition"
                                    title="Ver Perfil y Vacaciones"
                                >
                                    <span className="font-medium">
                                        Hola, { 
                                            (userData && typeof userData.nombreCompleto === 'string') 
                                            ? userData.nombreCompleto.split(' ')[0] 
                                            : (user?.email || 'Usuario') 
                                        } </span>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" /></svg>
                                </button>
                            ) : (
                                <span className="text-gray-600 hidden sm:flex items-center px-2 py-1 font-medium cursor-default">
                                    Hola, { 
                                        (userData && typeof userData.nombreCompleto === 'string') 
                                        ? userData.nombreCompleto.split(' ')[0] 
                                        : (user?.email || 'Usuario') 
                                    }
                                </span>
                            )}

                            {hasMultipleRoles && (
                                <div className="relative">
                                    <select 
                                        value={selectedRole || ""}
                                        onChange={(e) => setSelectedRole(e.target.value.toLowerCase())}
                                        className="appearance-none bg-gray-100 border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                                    >
                                        {userData.roles.map(role => (
                                            <option key={role} value={role.toLowerCase()} className="capitalize">
                                                {role.charAt(0).toUpperCase() + role.slice(1)}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {hasMultipleClients && activeRole === 'cliente' && (
                                <div className="relative">
                                    <select 
                                        value={selectedClientProfile?.id || user?.uid || ""}
                                        onChange={handleClientChange}
                                        className="appearance-none bg-gray-100 border border-gray-300 rounded-md py-2 pl-3 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-200"
                                    >
                                        <option value="" disabled>Selecciona una planta...</option>
                                        
                                        {clientProfiles.map(profile => (
                                            <option key={profile.id} value={profile.id}>
                                                {profile.planta || profile.nombreCompleto}
                                            </option>
                                        ))}
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                                        <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                                            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
                                        </svg>
                                    </div>
                                </div>
                            )}
                            
                            <button 
                                onClick={handleSafeSignOut} 
                                disabled={isWorking} 
                                className={`font-bold py-2 px-4 rounded-lg transition duration-300 ${
                                    isWorking 
                                    ? 'bg-gray-400 text-gray-700 cursor-not-allowed' 
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                }`}
                                title={isWorking ? "Debes 'Finalizar Día' antes de salir" : "Cerrar sesión"}
                            >
                                Salir
                            </button>
                        </div>
                    )}
                </div>
            </header>

            {showVacationModal && userData && canAccessVacations && (
                <UserVacationPortal 
                    user={user} 
                    userData={userData} 
                    onClose={() => setShowVacationModal(false)} 
                />
            )}
        </>
    );
};

//panel para ver y solicitar vacaciones 
const UserVacationPortal = ({ user, userData, onClose }) => {
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reason, setReason] = useState('');
    const [requestType, setRequestType] = useState('vacaciones');
    const [loading, setLoading] = useState(false);
    const [requests, setRequests] = useState([]);

    useEffect(() => {
        const q = query(collection(db, "solicitudesVacaciones"), where("uid", "==", user.uid));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setRequests(data);
        });
        return () => unsubscribe();
    }, [user.uid]);

    const balanceOficial = React.useMemo(() => {
        return calculateVacationBalance(
            userData.fechaIngreso, 
            userData.historialVacaciones || []
        );
    }, [userData]);

    const diasEnTramite = React.useMemo(() => {
        return requests
            .filter(r => r.estado && r.estado.includes('Pendiente') && (r.tipo === 'vacaciones' || !r.tipo))
            .reduce((acc, r) => acc + (Number(r.diasSolicitados) || 0), 0);
    }, [requests]);

    const saldoVisible = balanceOficial.disponibles - diasEnTramite;

    const { upcoming, history } = React.useMemo(() => {
        const now = new Date(); 
        now.setHours(0, 0, 0, 0);

        let pendingList = [];
        let rejectedList = [];
        let approvedFutureList = [];
        let approvedPastList = [];

        requests.forEach(req => {
            const item = {
                id: req.id,
                fechaInicio: req.fechaInicio,
                fechaFin: req.fechaFin,
                dias: req.diasSolicitados,
                motivo: req.motivo,
                estado: req.estado || 'Pendiente',
                motivoRechazo: req.motivoRechazo,
                tipo: req.tipo || 'vacaciones',
                isPending: req.estado && req.estado.includes('Pendiente')
            };

            if (req.estado === 'Rechazado') {
                rejectedList.push(item);
            } else if (item.isPending) {
                pendingList.push(item);
            }
        });

        const rawHistory = userData.historialVacaciones || [];
        rawHistory.forEach(reg => {
            const end = reg.fechaFin.toDate();
            end.setHours(0,0,0,0);
            
            const item = { ...reg, estado: end >= now ? 'Aprobado (Agendado)' : 'Completado', tipo: 'vacaciones' };

            if (end >= now) {
                approvedFutureList.push(item);
            } else {
                approvedPastList.push(item);
            }
        });

        const finalUpcoming = [...pendingList, ...approvedFutureList].sort((a, b) => 
            a.fechaInicio.seconds - b.fechaInicio.seconds
        );

        const finalHistory = [...rejectedList, ...approvedPastList].sort((a, b) => 
            b.fechaInicio.seconds - a.fechaInicio.seconds
        );

        return { upcoming: finalUpcoming, history: finalHistory };
    }, [requests, userData.historialVacaciones]);


    const handleRequest = async () => {
        if (!startDate || !endDate) return toast.error("Fechas incompletas");
        const start = new Date(startDate + 'T12:00:00');
        const end = new Date(endDate + 'T12:00:00');
        if (end < start) return toast.error("Fecha fin inválida");

        const businessDays = calculateBusinessDays(start, end);
        
        if (requestType === 'vacaciones') {
            if (businessDays === 0) return toast.error("No hay días hábiles en el rango seleccionado.");
            if (businessDays > saldoVisible) {
                return toast.error(`Saldo insuficiente. Disponibles: ${saldoVisible} días.`);
            }
        }

        setLoading(true);
        try {
            await addDoc(collection(db, "solicitudesVacaciones"), {
                uid: user.uid,
                nombre: userData?.nombreCompleto,
                rol: userData.rol || userData.roles?.[0],
                fechaSolicitud: Timestamp.now(),
                fechaInicio: Timestamp.fromDate(start),
                fechaFin: Timestamp.fromDate(end),
                diasSolicitados: businessDays,
                motivo: reason,
                tipo: requestType,
                estado: 'Pendiente Supervisor', 
                aprobadoSupervisor: false,
                aprobadoRH: false
            });
            toast.success("Solicitud enviada.");
            setStartDate(''); setEndDate(''); setReason(''); setRequestType('vacaciones');
        } catch (error) { toast.error("Error al solicitar."); }
        finally { setLoading(false); }
    };

    const formatDate = (ts) => ts ? ts.toDate().toLocaleDateString('es-MX') : '';
    
    const getStatusClass = (status) => {
        if (status === 'Rechazado') return 'bg-red-50 border-red-200';
        if (status && status.includes('Pendiente')) return 'bg-yellow-50 border-yellow-200';
        if (status && status.includes('Aprobado')) return 'bg-green-50 border-green-200';
        return 'bg-white border-gray-200';
    };

    const getTypeLabel = (tipo) => {
        switch(tipo) {
            case 'vacaciones': return { text: 'Vacaciones', class: 'bg-blue-100 text-blue-800' };
            case 'llegada_tarde': return { text: 'Llegada Tarde', class: 'bg-yellow-100 text-yellow-800' };
            case 'salida_anticipada': return { text: 'Salida Anticipada', class: 'bg-orange-100 text-orange-800' };
            case 'falta_justificada': return { text: 'Falta Justif.', class: 'bg-purple-100 text-purple-800' };
            case 'home_office': return { text: 'Home Office', class: 'bg-teal-100 text-teal-800' };
            default: return { text: tipo || 'Solicitud', class: 'bg-gray-100 text-gray-800' };
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl h-[85vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-gray-800">Portal de Solicitudes</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-2xl font-bold">&times;</button>
                </div>

                <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-xl p-6 text-white shadow-lg mb-6">
                    <div className="flex justify-between items-start">
                        <div>
                            <p className="text-blue-100 text-sm font-medium">Saldo Vacaciones</p>
                            <p className="text-5xl font-bold mt-1">{saldoVisible}</p>
                            <p className="text-xs text-blue-200 mt-1">Días disponibles</p>
                        </div>
                        <div className="text-right text-sm text-blue-100 space-y-1">
                            <p>Totales: <span className="font-bold text-white">{balanceOficial.totalGenerado}</span></p>
                            <p>Tomados: <span className="font-bold text-white">-{balanceOficial.tomados}</span></p>
                            {diasEnTramite > 0 && (
                                <p className="text-yellow-300 font-bold bg-white/10 px-2 py-0.5 rounded inline-block">
                                    En Trámite: -{diasEnTramite}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex-grow overflow-y-auto pr-2 space-y-6">
                    <div className="bg-gray-50 p-4 rounded-lg border">
                        <h4 className="font-bold text-gray-700 mb-3">Nueva Solicitud</h4>
                        {!userData.fechaIngreso ? <p className="text-red-500 text-sm">Sin fecha de ingreso. Contacta a RH.</p> : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-xs font-bold text-gray-500 mb-1">Tipo de Solicitud</label>
                                    <select 
                                        value={requestType} 
                                        onChange={(e) => setRequestType(e.target.value)}
                                        className="w-full p-2 border rounded-md bg-white focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="vacaciones">🏖️ Vacaciones</option>
                                        <option value="llegada_tarde">⏰ Permiso: Llegada Tarde</option>
                                        <option value="salida_anticipada">🏃 Permiso: Salida Anticipada</option>
                                        <option value="falta_justificada">🚑 Falta Justificada (Enfermedad/Luto)</option>
                                        <option value="home_office">🏠 Home Office</option>
                                    </select>
                                </div>

                                <div><label className="block text-xs font-bold text-gray-500">Desde</label><input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full p-2 border rounded-md"/></div>
                                <div><label className="block text-xs font-bold text-gray-500">Hasta</label><input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full p-2 border rounded-md"/></div>
                                <div className="md:col-span-2"><input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Motivo / Comentario" className="w-full p-2 border rounded-md"/></div>
                                <div className="md:col-span-2">
                                    <button onClick={handleRequest} disabled={loading} className="w-full bg-blue-600 text-white font-bold py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                                        {loading ? 'Enviando...' : 'Enviar Solicitud'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    {upcoming.length > 0 && (
                        <div>
                            <h4 className="font-bold text-gray-700 mb-2 text-sm uppercase tracking-wide">En Trámite / Agendadas</h4>
                            <div className="space-y-2">
                                {upcoming.map((req, idx) => {
                                    const typeInfo = getTypeLabel(req.tipo);
                                    return (
                                        <div key={idx} className={`border rounded-lg p-3 flex justify-between items-center ${getStatusClass(req.estado)}`}>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${typeInfo.class}`}>
                                                        {typeInfo.text}
                                                    </span>
                                                    <p className="font-bold text-gray-800 text-sm">
                                                        {formatDate(req.fechaInicio)} {req.fechaInicio?.seconds !== req.fechaFin?.seconds && `- ${formatDate(req.fechaFin)}`}
                                                    </p>
                                                </div>
                                                <p className="text-xs text-gray-600 pl-1">{req.dias} días • {req.motivo || 'Sin motivo'}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className={`px-2 py-1 text-xs font-bold rounded-full ${req.isPending ? 'bg-yellow-200 text-yellow-900' : 'bg-green-200 text-green-900'}`}>
                                                    {req.estado === 'Pendiente RH' || req.estado === 'Pendiente Supervisor' ? 'Pendiente' : req.estado}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                    <div>
                        <h4 className="font-bold text-gray-700 mb-2 text-sm uppercase tracking-wide">Historial Pasado</h4>
                        {history.length === 0 ? <p className="text-gray-400 text-sm italic">Sin historial.</p> : (
                            <div className="space-y-2">
                                {history.map((reg, idx) => {
                                    const typeInfo = getTypeLabel(reg.tipo);
                                    return (
                                        <div key={idx} className={`border rounded-lg p-3 flex justify-between items-start ${getStatusClass(reg.estado)}`}>
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                     <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${typeInfo.class}`}>
                                                        {typeInfo.text}
                                                    </span>
                                                    <p className={`font-medium text-sm ${reg.estado === 'Rechazado' ? 'text-red-800' : 'text-gray-800'}`}>
                                                        {formatDate(reg.fechaInicio)} - {formatDate(reg.fechaFin)}
                                                    </p>
                                                </div>
                                                
                                                <p className="text-xs text-gray-500 pl-1">{reg.motivo}</p>
                                                {reg.estado === 'Rechazado' && reg.motivoRechazo && (
                                                    <div className="mt-1 text-xs text-red-600 font-semibold bg-white px-2 py-1 rounded border border-red-100 inline-block">
                                                        Motivo rechazo: {reg.motivoRechazo}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right">
                                                {reg.estado === 'Rechazado' ? (
                                                    <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-1 rounded">Rechazado</span>
                                                ) : (
                                                    reg.tipo === 'vacaciones' && <p className="font-bold text-gray-500 text-sm">-{reg.dias}</p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};